// ---------------------------------------------------------------------------
// Prerender readiness signaling.
//
// The platform can serve a cached, headless-rendered HTML snapshot of chosen
// routes to crawlers and link unfurlers (Google, Bing, ChatGPT/Perplexity,
// Slack/iMessage/WhatsApp previews, etc.) while real users always get the live
// SPA. This is a pure SEO/GEO + share-preview win: bots that don't execute
// JavaScript see the real page content instead of the empty `<div id="root">`
// shell.
//
// Contract: for every route listed in web.json's `prerender.paths`, the SPA
// must set `data-prerender-ready="true"` on <html> once the route has resolved
// and its content is painted. The renderer waits for that marker before it
// captures the snapshot; a route that never sets it times out and the crawler
// falls back to the un-prerendered shell.
//
// This list drives the React readiness BEACON (below). It is intentionally a
// SUBSET of web.json's `prerender.paths`: the blog routes (`/blog`, `/blog/*`)
// are prerendered too, but they are DELIBERATELY excluded here because the blog
// renders by replacing the whole document (document.write) rather than via the
// React tree — so the beacon (a React component) is destroyed mid-render and
// can't signal for them. Instead DynamicBlog calls `signalPrerenderReady()`
// itself right after it writes the article, which is why that function does a
// fresh `document.documentElement` lookup (the pre-write element is stale after
// document.close()). Non-blog routes stay in sync with the sitemap's staticPaths
// (blog/render.mjs). Mostly genuinely public, static pages;
// /learn and /marketplace are public catalog pages whose SEO-critical header
// content (H1 + intro copy) renders synchronously — their lists hydrate
// client-side after the snapshot, which is fine. /spark's anonymous front door
// (SparkPage) is likewise a real landing page with a synchronous H1/hero, so it
// is prerendered and indexable. Interactive product surfaces that carry
// confidential idea content (/try), the unlisted A/B variants
// (/va–/vg, /v2), all authenticated app routes, and the blog (which is already
// emitted as real static .html files + sitemap at build time — see
// blog/plugin.mjs) are deliberately excluded.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import { PUBLIC_PAGE_PATHS } from '../../../methods/src/common/publicPages';

// Derived from the public-page registry (methods/src/common/publicPages.ts) — the
// single source of truth every public page runs through. Every registered page
// fires the readiness beacon, so this can no longer drift from ROUTE_SEO, the
// sitemap, or the breadcrumb labels (all derive from the same registry). The
// blog routes are prerendered too but are DELIBERATELY not in this set: the blog
// renders by replacing the whole document (document.write) rather than via the
// React tree — so the beacon (a React component) is destroyed mid-render and
// can't signal for them. Instead DynamicBlog calls `signalPrerenderReady()`
// itself right after it writes the article (see prerender note below).
const PRERENDER_ROUTES = new Set<string>(PUBLIC_PAGE_PATHS);

/** True when the given pathname is one of the routes we opt into prerendering. */
export function isPrerenderRoute(pathname: string): boolean {
  return PRERENDER_ROUTES.has(pathname);
}

/** Tell the prerender crawler this page is painted and safe to snapshot. */
export function signalPrerenderReady(): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-prerender-ready', 'true');
  }
}

// ---------------------------------------------------------------------------
// Deferred-content gate.
//
// Route-level code-splitting is already snapshot-safe: a lazy ROUTE suspends the
// whole router <Suspense>, so the beacon (a sibling of <Switch>) can't commit
// until the route's real content resolves. WITHIN-page splitting is different —
// if a prerendered page defers its below-the-fold sections into lazy chunks
// behind their OWN Suspense boundaries (so the hero paints without waiting for
// them), the outer boundary commits immediately and the beacon would fire
// `data-prerender-ready` while those sections are still loading. The crawler
// would then snapshot the page with its below-the-fold content missing.
//
// This gate lets such a page hold the snapshot. It opens the gate on mount and
// resolves it once its deferred chunks have loaded; the beacon awaits the gate
// before signalling. Real users are unaffected (they never wait on the marker);
// only the crawler waits, and only until the deferred content is in hand.
//
// One gate at a time is sufficient — exactly one prerendered page is mounted at
// once. Routes with no active gate resolve immediately, so their behaviour is
// unchanged.
let activeGate: { promise: Promise<void>; resolve: () => void } | null = null;

/**
 * Open the deferred-content gate. Call this as early as possible on a
 * prerendered page that defers below-the-fold content (a layout effect, so it
 * flushes before the beacon's passive effect reads the gate). Returns a resolver
 * to call once the deferred chunks have loaded — and always call it on unmount
 * too, so a gate can never be stranded open and hang a later route's snapshot.
 */
export function openPrerenderGate(): () => void {
  if (!activeGate) {
    let resolve!: () => void;
    const promise = new Promise<void>((r) => {
      resolve = r;
    });
    activeGate = { promise, resolve };
  }
  const gate = activeGate;
  return () => {
    gate.resolve();
    if (activeGate === gate) activeGate = null;
  };
}

/** Resolves once the deferred-content gate is released, or immediately if none is open. */
export function waitForPrerenderGate(): Promise<void> {
  return activeGate ? activeGate.promise : Promise.resolve();
}

/**
 * Mounted once at the App root. On a prerendered route, it waits until auth has
 * resolved (so gated pages have swapped their loader for real content) and the
 * route has painted, then flips the readiness marker. On every other route it
 * does nothing. Real users are unaffected — the attribute is inert to them.
 */
export function usePrerenderReadySignal(pathname: string, authReady: boolean): void {
  useEffect(() => {
    if (!authReady) return;
    if (!isPrerenderRoute(pathname)) return;
    let cancelled = false;
    let outer = 0;
    let inner = 0;
    // Wait for any deferred below-the-fold content to finish loading before we
    // tell the crawler to capture — otherwise its snapshot would miss it. On
    // routes with no deferred content the gate resolves immediately, so this is
    // the same two-frame-then-signal path as before. Then two frames so the
    // resolved route's DOM is actually painted before we mark it ready.
    waitForPrerenderGate().then(() => {
      if (cancelled) return;
      outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(signalPrerenderReady);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [pathname, authReady]);
}

/**
 * Prerender readiness as a COMPONENT, rendered INSIDE the router's <Suspense>
 * boundary (a sibling of <Switch>). When a lazy route is still loading, the
 * boundary shows its fallback and this beacon is NOT committed; once the route's
 * real chunk resolves and paints, the boundary commits its children and this
 * beacon's effect fires. So the crawler snapshot is always taken of the actual
 * page content, never the loading fallback — which is what makes route-level
 * code-splitting safe for prerendered public routes.
 */
export function PrerenderReadyBeacon({
  pathname,
  authReady,
}: {
  pathname: string;
  authReady: boolean;
}): null {
  usePrerenderReadySignal(pathname, authReady);
  return null;
}
